import { v4 as uuid4 } from 'uuid';
import {
  availableSearchSources,
  SearchResponseCollection,
  SearchResponseStatus,
  SearchResponseTrack,
  SearchResult,
  SearchType,
} from 'fym-common/src/types/searchService';
import { InspectResult, InspectType, inspectTypeToSearchType } from 'fym-common/src/types/inspectService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { ReleaseLinkData } from './types';

export function parseUrlToReleaseLinkData(url: string): ReleaseLinkData {
  return { id: uuid4(), data: { url }, type: SearchType.unknown };
}

function isTrackSearchResponse(data: any): data is SearchResult<SearchResponseTrack> {
  return data.type === SearchType.track;
}

function isTrackInspectResponse(data: any): data is InspectResult<SearchResponseTrack> {
  return data.type === InspectType.track;
}

export function parseSearchResultToReleaseLinkData(
  result: SearchResult<SearchResponseTrack | SearchResponseCollection>
): ReleaseLinkData | undefined {
  if (!result.data) {
    return undefined;
  }
  const artist = result.data.artistNames?.join(' ');
  return {
    id: uuid4(),
    type: result.type,
    source: result.source,
    data: isTrackSearchResponse(result)
      ? {
          track: result.data.name,
          album: result.data.albumName,
          artist,
          imageUrl: result.data.imageUrl,
          url: result.data.url,
        }
      : {
          album: result.data.name,
          artist,
          imageUrl: result.data.imageUrl,
          url: result.data.url,
        },
  };
}

export function parseInspectResultToReleaseLinkData(
  result: InspectResult<SearchResponseTrack | SearchResponseCollection>
): ReleaseLinkData | undefined {
  if (!result.data) {
    return undefined;
  }
  const artist = result.data.artistNames?.join(' ');
  return {
    id: uuid4(),
    type: inspectTypeToSearchType(result.type),
    source: result.source,
    data: isTrackInspectResponse(result)
      ? {
          track: result.data.name,
          album: result.data.albumName,
          artist,
          imageUrl: result.data.imageUrl,
          url: result.data.url,
          isrc: result.data.isrc,
        }
      : {
          album: result.data.name,
          artist,
          imageUrl: result.data.imageUrl,
          url: result.data.url,
        },
  };
}

/** Shape we need out of the musicapi `tracks.details` response. */
export type CatalogTrackDetails = {
  mainName: string;
  mainAlbumName: string | null;
  mainArtistName: string | null;
  /** Null when the catalog track has no image. */
  imageUrl: string | null;
  serviceLinks: { integrationType: string; url: string }[];
};

export type CatalogReleaseLinks = {
  /** Catalog links we already know, ordered like `availableSearchSources`. */
  releaseLinks: ReleaseLinkData[];
  /** Services the catalog has no link for — the only ones worth a live search. */
  missingSources: MusicAPIIntegrationID[];
};

/**
 * Turns a catalog track into ready-made release links, so the wizard only has
 * to search the services the catalog does not cover yet.
 */
export function parseCatalogTrackToReleaseLinks(track: CatalogTrackDetails): CatalogReleaseLinks {
  const sourceOrder = new Map<string, number>(availableSearchSources.map((source, index) => [source, index]));
  const knownLinks = track.serviceLinks
    .filter((link) => sourceOrder.has(link.integrationType))
    .sort((a, b) => (sourceOrder.get(a.integrationType) ?? 0) - (sourceOrder.get(b.integrationType) ?? 0));
  const coveredSources = new Set(knownLinks.map((link) => link.integrationType));

  return {
    releaseLinks: knownLinks.map((link) => ({
      id: uuid4(),
      type: SearchType.track,
      source: link.integrationType as MusicAPIIntegrationID,
      data: {
        track: track.mainName,
        album: track.mainAlbumName,
        artist: track.mainArtistName,
        imageUrl: track.imageUrl,
        url: link.url,
      },
    })),
    missingSources: availableSearchSources.filter((source) => !coveredSources.has(source)),
  };
}

export function parseSearchResults(
  items: SearchResult<SearchResponseTrack | SearchResponseCollection>[]
): ReleaseLinkData[] {
  return items.reduce((results, item) => {
    if (item.status !== SearchResponseStatus.success) {
      return results;
    }
    const parsedResult = parseSearchResultToReleaseLinkData(item);
    if (!parsedResult || !parsedResult.data.url) {
      return results;
    }
    results.push(parsedResult);
    return results;
  }, [] as ReleaseLinkData[]);
}
